import { useCallback, useEffect, useState } from 'react';
import { Modal, Platform, View } from 'react-native';
import { FullWindowOverlay } from 'react-native-screens';

import { Box } from '@squire/ui';

import { AnimatedToastItem } from './AnimatedToastItem';
import { WebPortal } from './WebPortal';
import { styles } from './styles';
import { toastManager } from './toastManager';
import type { Toast, ToastOverlayProps } from './types';

/**
 * ToastOverlay renders the toast notifications.
 * This component should be placed at the very top level of the app,
 * outside of any navigation to ensure toasts appear above all modals.
 *
 * Platform-specific behavior:
 * - iOS: Uses FullWindowOverlay from react-native-screens to render above native modals
 * - Android: Uses a transparent Modal with pointerEvents="box-none" to allow touch pass-through
 * - Web: Uses a portal to render at document.body with high z-index
 *
 * All platforms use pointerEvents="box-none" to allow touch pass-through.
 */
const ToastOverlay = ({ position = 'top' }: ToastOverlayProps) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const unsubscribe = toastManager.subscribe((newToasts) => {
      setToasts(newToasts);
    });

    return unsubscribe;
  }, []);

  const dismiss = useCallback((id: string) => {
    toastManager.dismiss(id);
  }, []);

  styles.useVariants({ position });

  const hasToasts = toasts.length > 0;

  const toastContent = (
    <Box style={styles.overlay} pointerEvents="box-none">
      <Box style={styles.container} pointerEvents="box-none">
        {toasts.map((t) => (
          <AnimatedToastItem
            key={t.id}
            toast={t}
            onDismiss={dismiss}
            position={position}
          />
        ))}
      </Box>
    </Box>
  );

  // On Web, use a portal to render at document.body with high z-index
  // This ensures toasts appear above all modals while allowing touch pass-through
  if (Platform.OS === 'web') {
    if (!hasToasts) return null;
    return (
      <WebPortal>
        <View style={styles.webContainer} pointerEvents="box-none">
          {toastContent}
        </View>
      </WebPortal>
    );
  }

  // On iOS, use FullWindowOverlay to render above native modals
  if (Platform.OS === 'ios') {
    if (!hasToasts) return null;
    return <FullWindowOverlay>{toastContent}</FullWindowOverlay>;
  }

  // On Android, use a transparent Modal with pointerEvents="box-none"
  // The Modal ensures proper z-index stacking above other modals
  // pointerEvents="box-none" on the container allows touches to pass through
  return (
    <Modal
      visible={hasToasts}
      transparent
      animationType="none"
      statusBarTranslucent
      // Prevent hardware back button from dismissing
      onRequestClose={() => {}}
      // This prop helps with touch pass-through on Android
      hardwareAccelerated
    >
      <View
        style={styles.androidContainer}
        pointerEvents="box-none"
        // This ensures touches pass through on Android
        collapsable={false}
      >
        {toastContent}
      </View>
    </Modal>
  );
};

ToastOverlay.displayName = 'ToastOverlay';

export { ToastOverlay };
